// A Saleor staff access token for callers outside the Apollo/@saleor/sdk graph.
//
// The access token @saleor/sdk holds is module-private, so anything talking to
// a non-Saleor service that verifies the token itself (the app-platform apps'
// dashboard APIs) has to mint its own from the refresh token in localStorage.
// This is that mint, in one place: it was copied per call site before, and
// each copy re-implemented the same cache.
//
// Two mutations, still one getter (WSM6-3065). A password session's refresh
// token is Saleor's own and renews through `tokenRefresh`. A Web Shop Manager
// session's refresh token was minted by the sign-in plugin (wsm-saleor,
// wsm/auth/oidc.py) and renews through `externalRefresh` with that plugin's
// id, which is what its `owner` claim says. The token that comes back is a
// Saleor staff access token either way, signed with the tenant's key, so the
// app-platform backends that verify one see no difference.
import { getApiUrl } from "@dashboard/config";

const REFRESH_TOKEN_STORAGE_KEY = "_saleorRefreshToken";

/** The owner claim on a token Saleor minted for itself. Anything else is a plugin's. */
const SALEOR_OWNER = "saleor";

// Saleor access tokens live 5 minutes by default. Renew slightly early so a
// token cannot expire in flight.
const EXPIRY_MARGIN_MS = 30_000;

// Used only when a token's own exp claim cannot be read, to keep a broken token
// from being cached indefinitely.
const FALLBACK_TTL_MS = 60_000;

const TOKEN_REFRESH_MUTATION = `
  mutation RefreshToken($refreshToken: String!) {
    tokenRefresh(refreshToken: $refreshToken) {
      token
    }
  }
`;

const EXTERNAL_REFRESH_MUTATION = `
  mutation ExternalRefresh($pluginId: String!, $input: JSONString!) {
    externalRefresh(pluginId: $pluginId, input: $input) {
      token
      refreshToken
    }
  }
`;

interface CachedToken {
  token: string;
  expiresAt: number;
}

let cachedToken: CachedToken | null = null;
let pendingMint: Promise<string | null> | null = null;

/**
 * The tenant's Saleor refresh token, or null when there is none, which is every
 * signed-out tab.
 *
 * The only read of this key left in the dashboard. A caller that has to present
 * the refresh token itself rather than an access token (the CMS pages backend
 * mints its own) takes it from here instead of reaching into localStorage a
 * second time, so there is one place that knows the key and one place to delete
 * when the last such backend is converted (WSM6-3065 AC1).
 */
export const getSaleorRefreshToken = (): string | null => {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Reads one claim off a JWT without verifying it. The signature is the callee's job. */
const readClaims = (token: string): Record<string, unknown> => {
  try {
    const payload = token.split(".")[1];

    if (!payload) {
      return {};
    }

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

    const claims = JSON.parse(window.atob(padded));

    return claims && typeof claims === "object" ? claims : {};
  } catch {
    return {};
  }
};

/**
 * The renewed refresh token, written where @saleor/sdk reads its own. A plugin's
 * refresh goes through the provider, which may rotate the token it holds inside; the
 * SDK's next refresh must then present the new one, or the session ends with it.
 */
const storeSaleorRefreshToken = (refreshToken: string): void => {
  try {
    window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  } catch {
    // Storage blocked: the SDK could not have stored its own either.
  }
};

const readExpiry = (token: string): number => {
  const { exp } = readClaims(token);

  return typeof exp === "number" ? exp * 1000 : 0;
};

/** The plugin that minted the refresh token, or null when Saleor minted it itself. */
const readOwner = (refreshToken: string): string | null => {
  const { owner } = readClaims(refreshToken);

  return typeof owner === "string" && owner && owner !== SALEOR_OWNER ? owner : null;
};

/**
 * Whether the session in this tab was started through an external sign-in (Web Shop
 * Manager) rather than a password. A backend that trades the refresh token for its own
 * access token cannot do that with a plugin's, so such callers send a bearer instead.
 */
export const isExternalSession = (): boolean => {
  const refreshToken = getSaleorRefreshToken();

  return !!refreshToken && readOwner(refreshToken) !== null;
};

const post = async (query: string, variables: Record<string, string>) => {
  const response = await fetch(getApiUrl(), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });

  return response.json();
};

/** Trade the refresh token in localStorage for an access token, by the mutation it owns. */
const mintAccessToken = async (): Promise<CachedToken | null> => {
  const refreshToken = getSaleorRefreshToken();

  if (!refreshToken) {
    return null;
  }

  try {
    const owner = readOwner(refreshToken);
    const body = owner
      ? await post(EXTERNAL_REFRESH_MUTATION, {
          pluginId: owner,
          input: JSON.stringify({ refreshToken }),
        })
      : await post(TOKEN_REFRESH_MUTATION, { refreshToken });
    const token =
      (owner ? body?.data?.externalRefresh?.token : body?.data?.tokenRefresh?.token) || null;
    const renewedRefreshToken = owner ? body?.data?.externalRefresh?.refreshToken : null;

    if (token && renewedRefreshToken) {
      storeSaleorRefreshToken(renewedRefreshToken);
    }

    return token
      ? { token, expiresAt: readExpiry(token) || Date.now() + FALLBACK_TTL_MS }
      : null;
  } catch {
    return null;
  }
};

/**
 * The current staff access token, or null when there is no session to mint from.
 *
 * Cached until shortly before it expires. `forceRefresh` drops the cache, which
 * is what a caller's own 401 means. Concurrent callers collapse onto a single
 * mint.
 */
export const getStaffAccessToken = async (forceRefresh = false): Promise<string | null> => {
  if (forceRefresh) {
    cachedToken = null;
  } else if (cachedToken && cachedToken.expiresAt - EXPIRY_MARGIN_MS > Date.now()) {
    return cachedToken.token;
  }

  // Collapse concurrent callers — the product grid fires several of these at
  // once — onto a single token request.
  if (!pendingMint) {
    pendingMint = mintAccessToken()
      .then(minted => {
        cachedToken = minted;

        return minted?.token ?? null;
      })
      .finally(() => {
        pendingMint = null;
      });
  }

  return pendingMint;
};

/** Test seam: drops the cached access token. */
export const resetStaffTokenCache = () => {
  cachedToken = null;
  pendingMint = null;
};
